import { AxiosPromise, AxiosResponse } from "axios";
import { oc } from "ts-optchain";

import { DealernetApi } from "./DealernetApi";
import { PartImagesApi } from "./PartImagesApi";

import {
    FreezeStatus,
    IFullPartDetailWithStatusDto,
    IKitComponentDto,
    IPagedList,
    IPartCrossReferenceDto,
    IRelatedPartDto,
    Nullable,
    SalesOrganization
} from "@/types";

export const CONTROLLER_PATH = "Part";

export class PartDetailServices {

    public static async getPartDetail(params: any): Promise<IFullPartDetailWithStatusDto | undefined> {
        const response = await DealernetApi.client().request({
            method: "get",
            url: `${CONTROLLER_PATH}/FullPartDetails`,
            params,
        });

        return oc(response as AxiosResponse<IFullPartDetailWithStatusDto>).data();
    }

    public static async getRelatedParts(params: any): Promise<IPagedList<IRelatedPartDto> | undefined> {
        const response = await DealernetApi.client().request({
            method: "get",
            url: `${CONTROLLER_PATH}/RelatedParts`,
            params,
        });

        return oc(response as AxiosResponse<IPagedList<IRelatedPartDto>>).data();
    }

    public static async getPartAlternatives(data: any): Promise<any> {
        const response = await DealernetApi.client().request({
            method: "post",
            url: `${CONTROLLER_PATH}/PartAlternatives`,
            data,
        });
        return oc(response).data();
    }

    public static async getPartMediaFiles(urlParams: any): Promise<any> {
        const response = await DealernetApi.client().request({
            method: "post",
            url: `${CONTROLLER_PATH}/GetPartMediaFiles`,
            params: urlParams,
        });

        return oc(response).data([]);
    }

    public static async getCrossReferences(params: any): Promise<IPartCrossReferenceDto[]> {
        const response = await DealernetApi.client().request({
            method: "get",
            url: `${CONTROLLER_PATH}/CrossReferences`,
            params,
        });

        return oc(response as AxiosResponse<IPartCrossReferenceDto[]>).data([]);
    }

    public static async getOrderHistoryOrdersByOemPartCode(urlParams: any, bodyParams: any): Promise<any> {
        const response = await DealernetApi.client().request({
            method: "post",
            url: `${CONTROLLER_PATH}/GetOrderHistoryOrdersByOemPartCode`,
            data: bodyParams,
            params: urlParams
        });
        const data = oc(response).data();
        return data;
    }

    public static async getOrderHistoryInvoicesByOemPartCode(urlParams: any, bodyParams: any): Promise<any> {
        const response = await DealernetApi.client().request({
            method: "post",
            url: `${CONTROLLER_PATH}/GetOrderHistoryInvoicesByOemPartCode`,
            data: bodyParams,
            params: urlParams
        });
        const data = oc(response).data();
        return data;
    }

    public static async getBuildableKits(urlParams: any): Promise<any> {
        const response = await DealernetApi.client().request({
            method: "get",
            url: `${CONTROLLER_PATH}/GetBuildableKits`,
            params: urlParams
        });
        const data = oc(response).data();
        return data;
    }

    public static async getOrderHistoryBackordersByOemPartCode(urlParams: any, bodyParams: any): Promise<any> {
        const response = await DealernetApi.client().request({
            method: "post",
            url: `backorder/SearchBackOrdersByPart`,
            data: bodyParams,
            params: urlParams
        });
        const data = oc(response).data();
        return data;
    }

    public static async getPartApplications(params: any) {
        const response = await DealernetApi.client().request({
            method: "get",
            url: `${CONTROLLER_PATH}/GetPartApplications`,
            params,
        });

        return oc(response).data([]);
    }

    public static async getCoreDeductions(params: any): Promise<any> {
        const response = await DealernetApi.client().request({
            method: "get",
            url: `${CONTROLLER_PATH}/GetCoreDeductions`,
            params,
        });

        return oc(response).data();
    }

    public static async getWhereUsed(params: any): Promise<any> {
        const response = await DealernetApi.client().request({
            method: "get",
            url: `${CONTROLLER_PATH}/GetWhereUsed`,
            params,
        });
        const data = oc(response).data([]);
        return data;
    }

    public static async getComponents(params: any): Promise<any> {
        const response = await DealernetApi.client().request({
            method: "get",
            url: `${CONTROLLER_PATH}/GetKitComponents`,
            params,
        });
        const data = oc(response).data([]);
        return data;
    }

    public static async getReplacementItems(params: any): Promise<any> {
        const response = await DealernetApi.client().request({
            method: "get",
            url: `${CONTROLLER_PATH}/GetReplacementItems`,
            params,
        });

        return oc(response).data([]);
    }

    public static async getSupersession(params: any): Promise<any> {
        const response = await DealernetApi.client().request({
            method: "get",
            url: `${CONTROLLER_PATH}/GetSupersessions`,
            params,
        });

        const data = oc(response).data([]);

        return data
    }

    public static async getPartImages(params: any): Promise<any>{
        const response = await PartImagesApi.client().request({
            method: "get",
            url: 'PartImages/GetPartImages/',
            params: params
        });

        return response.data;
    }

    public static emailPartImages(request: any): AxiosPromise<undefined> {
        return DealernetApi.client().request({
            method: "post",
            url: `${CONTROLLER_PATH}/SendPartImagesEmail/`,
            data: request,
        });
    }

    public static sendCrossAddEmail(urlParams: any, bodyParams: any): AxiosPromise<undefined> {
        return DealernetApi.client().request({
            method: "post",
            url: `${CONTROLLER_PATH}/SendCrossAddEmail`,
            data: bodyParams,
            params: urlParams
        });
    }

    public static sendRequestMoreInfoEmail(urlParams: any, bodyParams: any): AxiosPromise<undefined> {
        return DealernetApi.client().request({
            method: "post",
            url: `${CONTROLLER_PATH}/SendRequestMoreInfoEmail`,
            data: bodyParams,
            params: urlParams
        });
    }

    public static sendCrossBreakEmail(urlParams: any, bodyParams: any): AxiosPromise<undefined> {
        return DealernetApi.client().request({
            method: "post",
            url: `${CONTROLLER_PATH}/SendCrossBreakEmail`,
            data: bodyParams,
            params: urlParams
        });
    }

    public static suggestPartApplication(urlParams: any, bodyParams: any): AxiosPromise<undefined> {
        return DealernetApi.client().request({
            method: "post",
            url: `${CONTROLLER_PATH}/SendApplicationSuggestionEmail`,
            data: bodyParams,
            params: urlParams
        });
    }

    public static sendPricingRequestEmail(urlParams: any, bodyParams: any): AxiosPromise<undefined> {
        return DealernetApi.client().request({
            method: "post",
            url: `${CONTROLLER_PATH}/SendPricingRequestEmail`,
            params: urlParams,
            data: bodyParams
        });
    }

    public static async getKitBuildInfo(params: any, dto: IFullPartDetailWithStatusDto) {
        if (dto.SalesOrganization === SalesOrganization.RP) {
            const kitBuildResponse = await DealernetApi.client().request({
                method: "get",
                url: `${CONTROLLER_PATH}/GetKitComponents`,
                params: {
                    accountId: params.accountId,
                    userId: params.userId,
                    mcfCostCenterId: dto.McfCostCenterId,
                    oemPartCode: dto.OemPartCode,
                },
            });

            return oc(kitBuildResponse as AxiosResponse<IKitComponentDto[]>).data([]);
        }

        return null;
    }

    private static getDiscountPrice(dto: IFullPartDetailWithStatusDto): Nullable<number> {
        const discounts = oc(dto).PromoDiscounts([]);

        const originalPrice = dto.DealerNetPrice;

        const promoDiscount = oc(discounts.filter((x) => x.OriginalPricePerUnit === originalPrice))[0]();

        if (promoDiscount && promoDiscount.Discount) {
            return originalPrice - promoDiscount.Discount;
        }

        return null;
    }

    private static isPartsFastFreeEligible(dto: IFullPartDetailWithStatusDto): boolean {
        return !(
            dto.IsHazardous ||
            dto.IsMadeAsOrdered ||
            dto.IsVendorNonStockProgram ||
            dto.FreezeStatus === FreezeStatus.H ||
            dto.FreezeStatus === FreezeStatus.S ||
            dto.ItemCategory === "ZDSH"
        );
    }
}

export interface IAlternativesListRequestDto {
    AccountId: number;
    UserId: number;
    PartId: number;
    OemPartCode: string;
    UserAudiences: number[];
    ValidateAlternatives: boolean;
}